/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { LanguageCodeHelper } from '@app/app/shared/helpers/language-code-helper';
import { BadgeResponse } from '@app/app/shared/models';
import { BadgeDataService, PostBadgeRequest } from '@app/app/shared/services/badge/badge-data.service';
import { RestClientService } from '@app/app/shared/services/rest-client.service';
import { ObservableResult } from '@app/app/shared/store';
import { environment } from '@app/environments/environment';
import { TranslocoService } from '@ngneat/transloco';
import { catchError, switchMap } from 'rxjs/operators';

@Injectable()
export class ApiBadgeDataService implements BadgeDataService {
  constructor(
    private restClient: RestClientService,
    private translocoService: TranslocoService,
  ) {}

  createBadge(request: PostBadgeRequest, languageCode?: string): ObservableResult<BadgeResponse> {
    return this.restClient
      .post<BadgeResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', '/badges'),
        request,
        null,
        languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(languageCode) : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorCreateBadge'))),
      );
  }

  updateBadge(badgeId: string, request: PostBadgeRequest, languageCode?: string): ObservableResult<BadgeResponse> {
    return this.restClient
      .patch<BadgeResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', `badges/${badgeId}`),
        request,
        null,
        languageCode ? LanguageCodeHelper.checkAndGetContentLanguageCode(languageCode) : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorUpdateBadge'))),
      );
  }

  getBadge(badgeId: string, languageCode?: string): ObservableResult<BadgeResponse> {
    return this.restClient
      .get<BadgeResponse>(
        Location.joinWithSlash(environment.apiRootUrl || '', `/badges/${badgeId}`),
        null,
        languageCode ? { 'Accept-Language': languageCode } : null,
      )
      .pipe(
        switchMap(({ body }) => ObservableResult.ofSuccess(body)),
        catchError(() => ObservableResult.ofError(this.translocoService.translate('translations.errors.errorGetBadge'))),
      );
  }
}
